import { UIIcon } from '../config/icons';
import { ABProgressView } from '../lib/ab/widgets';
import { FAsset, fAssetCreate, FAssetGroup, fAssetGroupsGetAll, FAssetKind, fAssetsGetAll } from '../lib/mdl/f-asset';
import { fIconsUploadFile } from '../lib/mdl/f-icons';
import { UIButton } from '../lib/ui/component/button';
import { UIDialog } from '../lib/ui/component/dialog';
import { UIImageInput } from '../lib/ui/component/image';
import { UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIMediaInput } from '../lib/ui/component/mediainput';
import { UISelect } from '../lib/ui/component/select';
import { uiToast } from '../lib/ui/component/toast';
import { UIContent, UIDropDelegate, uiInitDropTarget } from '../lib/ui/dnd';
import { $el } from '../lib/ui/elements';
import { ui_font_accept, uiMakeFakeUploadInput } from '../lib/ui/functions';
import { UIScreen } from '../lib/ui/screen';
import { UIValue } from '../lib/ui/types';
import { UIView, UIViewAddSpec, UIViewSpec } from '../lib/ui/view';
import { $log } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { $form } from '../lib/y/form';
import { $i18n } from '../lib/y/i18n';
import { $location } from '../lib/y/location';

interface Group {
    key: string;
    group: FAssetGroup;
    children: Group[];
    assets: FAsset[];
}

function makeGroup(group: Group): UIViewAddSpec {
    const arrow = $el.DIV('r-arrow', $el.ICON(UIIcon.ANGLE_RIGHT));
    const child = $el.DIV('tl-translations-block');
    const title = $el.DIV('r-text', group.group.name || group.key)
    const header = $el.DIV('tl-translations-category', [
        arrow,
        title,
        new UIButton({
            icon: UIIcon.PLUS, classes: 'ui-tint-fg-80 m-no-padding m-left', on_click: () => {
                new AssetDialog(group.group.key).present().then(() => {
                    $location.reload();
                });
            }
        })
    ]);

    for(const c of group.children) {
        child.ui_add(makeGroup(c));
    }

    for(const a of group.assets) {
        child.ui_add(makeAsset(a))
    }

    let expanded = false;
    child.ui_hide();

    const on_click = () => {
        expanded = !expanded;
        if(expanded) {
            child.ui_show();
            header.classList.add('f-expanded');
        } else {
            child.ui_hide();
            header.classList.remove('f-expanded');
        }
    }

    arrow.addEventListener('click', on_click);
    title.addEventListener('click', on_click);

    return [header, child];
}

function makeAsset(asset: FAsset): UIViewAddSpec {
    const row = $el.DIV('tl-translations-value', $el.DIV('r-key', asset.name || asset.key));

    // all_rows.push({
    //     commit: commits => {
    //         const commit: TLTranslationCommit = { key: item.key, update: {} };
    //         let some_changed = false;

    //         for(const lang of languages) {
    //             const inputs = lang_inputs[lang]!;
    //             const new_value: string[] = [];
    //             let changed = false;

    //             for(let i = 0; i < item.size; i++) {
    //                 const value = inputs[i].value;
    //                 if(value != item.value[lang]?.[i] || '') {
    //                     changed = true;
    //                 }
    //                 new_value.push(value);
    //             }

    //             if(changed) {
    //                 commit.update[lang] = new_value;
    //                 some_changed = true;
    //             }
    //         }

    //         if(some_changed) {
    //             commits.push(commit);
    //         }
    //     }
    // });

    return row;
}

const kind_values: UIValue<FAssetKind>[] = [{ key: 'image', name: 'Image' }, { key: 'font', name: 'Font' }];

class AssetDialog extends UIDialog {
    constructor(group: string, _asset?: FAsset) {
        super({ classes: 'm-600 m-mobile-fs' });

        const key = new UITextInput({ required: true });
        const name = new UITextInput({ required: true });
        const kind = new UISelect(kind_values, { required: true });
        const object_image = new UIImageInput({ required: true, scope: 'asset', size: 'contain', new_accept: true });
        const object_file = new UIMediaInput({ required: true, scope: 'asset', accept: ui_font_accept });
        const width = new UINumberInput({ required: true });
        width.value = 0;
        const height = new UINumberInput({ required: true });
        height.value = 0;
        const aspect = new UINumberInput({ required: true, decimal: true });
        aspect.value = 0;
        const duration = new UINumberInput({ required: true, decimal: true });
        duration.value = 0;

        $on(kind, 'value', v => {
            if(v == 'image') {
                object_image.ui_show();
                object_file.ui_hide();
            } else if(v == 'font') {
                object_image.ui_hide();
                object_file.ui_show();
            }
        });

        $on(object_file, 'value', () => {
            const file_name = object_file.file_name;
            if(file_name && key.value.trim() == '') {
                const we = file_name.replace(/\.[^/.]+$/, ''); //remove extension
                key.value = we;
                name.value = we;
            }
        });

        const form = $form({ key, name, kind, width, height, aspect, duration });

        const buttons = $el.DIV('l-button-row m-right', [
            new UIButton({
                title: $i18n.tl.b_cancel, classes: 'm-hollow', on_click: () => {
                    this.dismiss(null);
                }
            }),
            new UIButton({
                title: "Create", classes: 'ui-tint-reverse', on_click: () => {
                    if(!form.validate()) return;
                    const value = form.value as FAsset;
                    value.group_key = group;
                    if(kind.value == 'image') {
                        if(!$form.validate(object_image)) return;
                        value.object_id = object_image.value!;
                    } else if(kind.value == 'font') {
                        if(!$form.validate(object_file)) return;
                        value.object_id = object_file.value!;
                    } else {
                        return;
                    }

                    return fAssetCreate(value).then(() => {
                        this.dismiss(true);
                    });
                }
            })
        ]);

        this.content.ui_add($el.DIV('w-block l-form tl-producer-form m-compact', [
            new UILabeled(key, { above: 'Key' }),
            new UILabeled(name, { above: 'Name' }),
            new UILabeled(kind, { above: 'Kind' }),
            new UILabeled(object_image, { above: 'Object' }),
            new UILabeled(object_file, { above: 'Object' }),
            $el.DIV('l-input-row', [
                new UILabeled(width, { above: 'Width' }),
                new UILabeled(height, { above: 'Height' })
            ]),
            $el.DIV('l-input-row', [
                new UILabeled(aspect, { above: 'Aspect Ratio' }),
                new UILabeled(duration, { above: 'Duration' })
            ]),
            buttons
        ]));

        kind.value = 'image';
    }
}

export class AdminAssetsScreen extends UIScreen {
    constructor() {
        super();

        this.title = 'Assets';
    }

    protected override async fillContent(content: UIViewSpec) {
        const [all_groups, all_assets] = await Promise.all([fAssetGroupsGetAll(), fAssetsGetAll()]);

        all_groups.sort((a, b) => a.key.localeCompare(b.key));

        const root: Group[] = [];
        const all: { [key: string]: Group } = {};

        for(const group of all_groups) {
            const split = group.key.split('.');

            let parent_array: Group[] = root;

            for(let i = 0; i < split.length; i++) {
                const part = split[i];
                if(i == split.length - 1) {
                    const new_group = { key: part, group: group, children: [], assets: [] };
                    parent_array.push(new_group);
                    all[group.key] = new_group;
                } else {
                    //because of sorting, parent can only be the last one
                    if(parent_array.length == 0)
                        throw 'Invalid data';
                    const parent = parent_array[parent_array.length - 1];
                    if(parent.key != part) {
                        throw 'Invalid data';
                    }
                    parent_array = parent.children;
                }
            }
        }

        for(const asset of all_assets) {
            const group = all[asset.group_key];
            if(!group)
                throw 'Invalid data';
            group.assets.push(asset);
        }

        const grid = $el.DIV('tl-translations-grid');

        for(const group of root) {
            grid.ui_add(makeGroup(group));
        }

        content.ui_add($el.SECTION('', [
            $el.DIV('l-h-scroll w-block b', grid)
        ]));
    }
}

class IconsButton extends UIView implements UIDropDelegate {
    private _text: HTMLElement;
    private _got_file?: boolean;

    constructor() {
        super('div', 'tl-pdf-button');
        uiInitDropTarget(this);

        this.ui_add(this._text = $el.B(['Drag FA file', $el.BR(), 'or', $el.BR(), 'click to upload']));

        const upload_input = uiMakeFakeUploadInput(files => {
            this._upload(files[0]);
        }, ['application/zip'])

        this.root.addEventListener('click', () => {
            if(!this._got_file) {
                upload_input.click();
            }
        });
    }

    private _upload(file: File) {
        this._got_file = true;
        this._text.remove();
        const progress = new ABProgressView;
        this.ui_add(progress);
        progress.text = 'Uploading';

        $log('app', 'uploading', file.name)

        fIconsUploadFile(file, p => { progress.progress = p }).then(() => {
            progress.complete("Done!");
        }, m => {
            uiToast(m);
        });
    }

    dropEnter() {
        return !this._got_file;
    }

    drop(content: UIContent) {
        if(this._got_file) return;

        if(content.type === 'files') {
            const files = content.value as File[];
            this._upload(files[0])
        }
    }
}

export class AdminIconsScreen extends UIScreen {
    protected override fillContent(content: UIViewSpec): void {
        const button = new IconsButton;

        content.ui_add($el.DIV('ui-section w-block l-form m-center', [
            button,
        ]));
    }
}
